import {Component, OnDestroy, PLATFORM_ID, inject, OnInit} from '@angular/core';
import {HupSubscriptionsService} from "../../../data/services/hup-subscriptions.service";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {TranslationService} from "../../../core/services/translation.service";
import {BaseData} from "../../../data/models/base-data.model";
import {BaseDataService} from "../../../core/services/base-data.service";
import {Offer} from "../../../data/models/offer.model";
import {OfferService} from "../../../data/services/offer.service";
import {NavigationCommand, NavigationService} from "../../../data/services/navigation.service";
import {RedirectService} from "../../../data/services/redirect.service";
import {ConfigService} from "../../../core/services/config.service";
import {interval, Subscription, timer} from 'rxjs';
import {LoginService} from "../../../core/components/login/login.service";
import {DatePipe, DecimalPipe, isPlatformBrowser, NgOptimizedImage} from '@angular/common';
import {LoadingSpinnerComponent} from "../../../core/components/loading-spinner/loading-spinner.component";
import {FitImageDirective} from "../../../core/directives/fit-image.directive";
import {BankAccountPipe} from "../../../core/pipes/bank-account.pipe";
import {StripHtmlPipe} from "../../../core/pipes/strip-html.pipe";
import {MatButton} from "@angular/material/button";
import {MatCard, MatCardSubtitle} from "@angular/material/card";
import {ApplyIfNotEmptyDirective} from "../../../core/directives/apply-if-not-empty.directive";
import {OfferPrice} from "../../../data/models/price.model";
import {ImageService} from '../../../core/services/image.service';
import {HouseNo} from "../../../data/models/houseno/houseno";
import {SelectedGift} from "../../../data/models/selected-gift.model";
import {BonusType} from "../../../data/models/bonus.model";
import {Address} from "../../../data/models/address.model";
import {AddressCode} from "../../../data/models/address-code.model";
import {Person} from "../../../data/models/person.model";
import {isDigital} from "../../../core/utils/offer-utils";
import {
  bonusAddressTranslationKey,
  configFlag,
  CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS,
  CONFIG_SHOW_DELIVERY_ADDRESS,
  showDeliveryAddressBlock
} from "../../../core/utils/digital-order-address";
import {titleDescription} from "../../../core/utils/address-utils";

@Component({
  selector: 'app-offer-ordered-overview',
  templateUrl: './offer-ordered-overview.component.html',
  styleUrls: ['./offer-ordered-overview.component.scss'],
  imports: [
    LoadingSpinnerComponent,
    FitImageDirective,
    DatePipe,
    BankAccountPipe,
    MatButton,
    MatCard,
    ApplyIfNotEmptyDirective,
    DecimalPipe,
    NgOptimizedImage,
    StripHtmlPipe
  ],
  providers: [
    DatePipe
  ],
  standalone: true
})
export class OfferOrderedOverviewComponent implements OnInit, OnDestroy {
  private subscriptionService = inject(HupSubscriptionsService);
  private route = inject(ActivatedRoute);
  navigationService = inject(NavigationService);
  translationService = inject(TranslationService);
  private baseDataService = inject(BaseDataService);
  private offerService = inject(OfferService);
  private redirectService = inject(RedirectService);
  private configService = inject(ConfigService);
  private loginService = inject(LoginService);
  private platformId = inject<Object>(PLATFORM_ID);
  imageService = inject(ImageService);

  order: any;
  offer: Offer;
  product: BaseData;
  price : OfferPrice[];
  selectedGifts: SelectedGift[] = [];
  /** Prämie des Bestellers - liefert die Prämienlieferadresse. */
  bonusRecipient: SelectedGift;
  /** Fremdprämie - liefert die Werberadresse. */
  advertiserRecipient: SelectedGift;
  isUserLoggedIn: boolean = false;
  allPaymentFrequencies: BaseData[] = [];
  allFormsOfPayment: BaseData[] = [];
  allProducts: BaseData[] = [];
  redirectUrl: string;
  timeUntilRedirectSeconds: number = 0;
  timerSub: Subscription;
  // Die Bestätigungsseite wird direkt nach dem Bestellabschluss aufgerufen und lädt ihre Daten erst
  // nachgelagert in zwei Schritten (Abo, dann Angebot; bei Gast-Bestellungen zusätzlich erst nach dem
  // OTLC-Login). Bis beides da ist, steht an der Stelle der Details ein Spinner - würden die Details
  // schon nach dem Abo erscheinen, liefen Preis und Angebotstexte sichtbar nach (P5W-4410).
  loading: boolean = true;

  /**
   * Die Aboart der Bestellung.
   *
   * Es gibt zwei Quellen: die Aboart am Abo und die Angebotsart am nachgeladenen Angebot. Beide
   * können fehlen - 'subscriptionType' führt nur ein ExistingSubscription, und das Angebot wird nur
   * nachgeladen, wenn es Zahlungsdaten gibt.
   *
   * Entscheidend ist aber etwas anderes: Im Backend ist die Aboart ein OfferType mit primitiven
   * boolean-Feldern (ePaper, paidContent). Sie liegt deshalb auch dann als vollständig gefülltes
   * Objekt vor, wenn sie nie ermittelt wurde - und behauptet dort "nicht digital". Ein '??' fiele
   * darauf herein und die Seite hielte ein Digital-Abo für Print. Deshalb zählt hier, was eine der
   * Quellen POSITIV meldet.
   */
  private orderedOfferType(): { ePaper: boolean, paidContent: boolean } {
    return [this.order?.subscriptionType, this.offer?.offerType].find(type => isDigital(type))
      ?? {ePaper: false, paidContent: false};
  }

  /**
   * Soll die Lieferadresse ausgewiesen werden?
   *
   * Digitale Abos werden nicht zugestellt. Die Bestellung trägt als Lieferadresse trotzdem die
   * Rechnungsadresse (s. SummaryStepComponent) - stünde sie hier, hätte der Kunde dieselbe Anschrift
   * zweimal vor sich. Ob das so ist, entscheidet dieselbe Konfiguration wie in der Bestellübersicht,
   * damit Bestellstrecke und Bestätigung nicht auseinanderlaufen.
   */
  showDeliveryAddress(): boolean {
    return showDeliveryAddressBlock(
      this.orderedOfferType(),
      configFlag(this.configService.getConfig(CONFIG_SHOW_DELIVERY_ADDRESS), true));
  }

  /**
   * Titel der Prämienadresse. Bei einem digitalen Abo mit Sachprämie ist sie die einzige
   * Lieferanschrift der Bestellung und kann per Konfiguration auch so benannt werden.
   */
  bonusAddressTitle(): string {
    return this.translationService.getTranslation(bonusAddressTranslationKey(
      this.orderedOfferType(),
      configFlag(this.configService.getConfig(CONFIG_BONUS_ADDRESS_IS_DELIVERY_ADDRESS), false)));
  }

  ngOnInit(): void {
    const configService = this.configService;
    const platformId = this.platformId;

    this.route.paramMap.subscribe((params) => {
      this.allPaymentFrequencies = this.baseDataService.getBaseData('invoicemethods');
      this.allFormsOfPayment = this.baseDataService.getBaseData('paymentmethods');
      this.allProducts = this.baseDataService.getBaseData('products');

      if (isPlatformBrowser(platformId)) {
        this.loginService.getAuthData().subscribe(authData => {
          if (!!authData && (authData.token !== null)) {
            this.handleParams(params);
          }
        }, () => {
          this.loading = false;
        });
      }
    });
    this.route.queryParamMap.subscribe((queryParams) => {
      this.redirectUrl = queryParams.get("redirect_URL");
    });
    const timeUntilRedirect = configService.getConfig("order.timeUntilRedirect");
    if (this.redirectUrl?.length > 0 && Number(timeUntilRedirect?.value) > 0) {
      this.timeUntilRedirectSeconds = Number(timeUntilRedirect.value);

      const intervalSubscription = interval(1000).subscribe(() => {
        this.timeUntilRedirectSeconds = Math.max(0, --this.timeUntilRedirectSeconds);
        if (this.timeUntilRedirectSeconds == 0) {
          intervalSubscription.unsubscribe();
        }
      });

      const source = timer(this.timeUntilRedirectSeconds * 1000);
      this.timerSub = source.subscribe(() => {
        this.redirectService.redirect(this.redirectUrl);
      });
    }
  }

  private handleParams(params: ParamMap) {
    this.subscriptionService.getSubscription(params.get('webId'), params.get('backendId')).subscribe({
      next: (subscription) => {
        this.order = subscription;
        this.splitHouseNo(this.order.curBilling);
        this.splitHouseNo(this.order.curDelivery);
        if (subscription.paymentList?.length > 0) {
          this.order.curPayment = subscription.paymentList[0];
          const paymentMethod = this.allFormsOfPayment.find(method => method.key === this.order.curPayment.paymentType.paymentMethod);
          this.order.curPayment.paymentType.payMentMethodDescription = paymentMethod?.description;
          const paymentFrequency = this.allPaymentFrequencies.find(frequency => frequency.key === this.order.curPayment.paymentType.paymentFrequency);
          this.order.curPayment.paymentType.paymentFrequencyDescription = paymentFrequency?.description;
          this.offerService.getOfferDetail(+params.get('orderChoiceKey')).subscribe({
            next: offer => {
              this.offer = offer ?? this.order.offer;
            },
            error: () => {
              this.offer = this.order.offer;
              this.loading = false;
            },
            complete: () => {
              this.product = this.allProducts.find(product => product.key === this.offer?.product?.productCode);
              this.loading = false;
            }
          });
        } else {
          // Ohne Zahlungsdaten wird das Angebot nicht nachgeladen - dann ist hier schon alles da.
          this.loading = false;
        }
        const id = subscription.backendId > 0 ? subscription.backendId : subscription.webId;
        this.price = this.subscriptionService.getPriceForKey(id);
        this.selectedGifts = (this.order.selectedGiftList ?? []).filter((gift: SelectedGift) => !!gift?.articleDescription);
        this.resolveGiftRecipients(this.selectedGifts);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  /**
   * Zahlart und Zahlweise in einer Zeile. Tragen beide dieselbe Beschreibung (z. B. bei kostenlosen
   * Angeboten "kostenlos" / "kostenlos"), wird sie nur einmal ausgegeben - die Schreibweise pflegt jeder
   * Mandant selbst, deshalb ohne Rücksicht auf Groß-/Kleinschreibung. Leere Bestandteile fallen raus,
   * damit kein einzelner Schrägstrich stehen bleibt.
   */
  paymentDescription(): string {
    const paymentType = this.order?.curPayment?.paymentType;
    const parts: string[] = [paymentType?.payMentMethodDescription, paymentType?.paymentFrequencyDescription]
      .map((part: string) => part?.trim())
      .filter((part: string) => !!part);
    return parts
      .filter((part, index) => parts.findIndex(other => other.toLowerCase() === part.toLowerCase()) === index)
      .join(' / ');
  }

  /**
   * Anrede, Name und Anschrift des Prämienempfängers in einer Zeile - in Österreich zusätzlich mit den
   * Hausnummern-Bestandteilen Stock, Stiege und Tür. Leere Bestandteile fallen raus, damit keine
   * Lücken in der Zeile stehen.
   */
  recipientAddressLine(gift: SelectedGift): string {
    const address = gift?.address;
    if (!address) {
      return '';
    }
    const houseNo = address.country === 'A'
      ? this.joinParts([address.houseno, address.stock, address.stiege, address.tuer])
      : address.houseno;
    return this.joinParts([
      this.salutationDescription(gift.person), this.personTitle(gift.person), gift.person?.firstname, gift.person?.lastname,
      address.street, houseNo, address.zipcode, address.city
    ]);
  }

  /**
   * Die Anrede-Beschreibung, die handlePerson über den Kundentyp auflöst. Prämien, die vor der Abbildung
   * auf den Kundentyp (SubscriptionOrderConverter.toClientTypeKey) bestellt wurden, tragen in salutation
   * noch den Schlüssel der addresscodes - für diesen Altbestand wird die Anrede direkt dort nachgeschlagen.
   */
  private salutationDescription(person: Person): string {
    if (person?.addressCodeDescription) {
      return person.addressCodeDescription;
    }
    const addressCodes = this.baseDataService.getBaseData<AddressCode>('addresscodes') ?? [];
    return addressCodes.find(addressCode => addressCode.key === person?.salutation)?.description;
  }

  /** Der Titel der Person für die Anzeige - das Abo liefert nur den Schlüssel, s. {@link titleDescription}. */
  personTitle(person: Person): string {
    return person?.titleDescription || titleDescription(person?.title, this.baseDataService.getBaseData('titles'));
  }

  private joinParts(parts: string[]): string {
    return parts.filter(part => !!part?.trim()).join(' ');
  }

  /**
   * Sucht die Prämienempfänger heraus, deren Anschrift die Bestätigung ausweisen soll. Welche Adresse an
   * einer Prämie hängt, hat schon die Bestellung entschieden: Bei einer Fremdprämie steht dort der Werber,
   * sonst der Besteller mit seiner Prämienlieferadresse (s. SubscriptionOrderConverter.convertBonusData).
   * Geldprämien tragen keine Anschrift und liefern deshalb keinen Eintrag.
   *
   * Mehrere Prämien desselben Typs teilen sich denselben Empfänger - die erste passende genügt.
   */
  private resolveGiftRecipients(gifts: SelectedGift[]): void {
    this.bonusRecipient = this.findGiftRecipient(gifts, BonusType.NON_MONEY_BONUS);
    this.advertiserRecipient = this.findGiftRecipient(gifts, BonusType.THIRD_PARTY_NON_MONEY_BONUS);
  }

  private findGiftRecipient(gifts: SelectedGift[], bonusType: BonusType): SelectedGift {
    const recipient = gifts.find(gift => gift.bonusType === bonusType && this.hasCompleteAddress(gift.address));
    if (recipient) {
      this.baseDataService.handlePerson(recipient.person);
      this.splitHouseNo(recipient);
    }
    return recipient;
  }

  /**
   * Ausgewiesen wird nur eine vollständige Anschrift - eine halbe Zeile neben dem Label ist keine
   * Information, sondern ein Fehlerbild.
   */
  private hasCompleteAddress(address: Address): boolean {
    return !!address?.street?.trim() && !!address?.houseno?.trim()
      && !!address?.zipcode?.trim() && !!address?.city?.trim();
  }

  private splitHouseNo(orderAddress: {address?: Address}): void {
    const address = orderAddress?.address;
    if (!address?.houseno) {
      return;
    }
    const houseNoParts = address.houseno;
    address.houseno = HouseNo.getHouseNumber(houseNoParts);
    address.stock = HouseNo.getStock(houseNoParts);
    address.stiege = HouseNo.getStiege(houseNoParts);
    address.tuer = HouseNo.getTuer(houseNoParts);
  }

  routeToOrderList(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION);
  }

  ngOnDestroy(): void {
    if (this.timerSub && !this.timerSub?.closed) {
      this.timerSub?.unsubscribe();
    }
  }
}
